import { useQuery } from "@tanstack/react-query";
import type { TeamScope, TeamSummary } from "@/lib/persons/team-summary";
import { personKeys } from "@/lib/query";

/**
 * A manager's direct reports — or, for an admin asking for `company` scope,
 * every active person — with their guidance and logged time, proxied from the
 * Nymbl API Gateway by `/api/persons/:id/team-summary`.
 *
 * The scope is part of the query KEY, so switching keeps both payloads cached
 * and switching back is instant. It matters here more than usual: company scope
 * is ~3s of gateway time, and re-fetching it every time someone glanced at
 * their own team would make the switch feel broken.
 *
 * Like the Current Projects tab this is a live read of NOS rather than a cached
 * copy in our database, so it gets a longer `staleTime` — the data changes on
 * NOS's schedule, and refetching on every window focus would spend a
 * rate-limited gateway call for nothing.
 */
export function useTeamSummary(managerId: string | undefined, scope: TeamScope = "team") {
  return useQuery({
    queryKey: personKeys.teamSummary(managerId ?? "", scope),
    enabled: Boolean(managerId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<TeamSummary> => {
      const res = await fetch(
        `/api/persons/${managerId}/team-summary?scope=${encodeURIComponent(scope)}`,
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
          body !== null && typeof body === "object" && "error" in body
            ? String(body.error)
            : "Could not load the team";
        throw new Error(message);
      }
      const json = await res.json();
      return json.data;
    },
  });
}
